import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MetricsChart } from '../../components/observability/MetricsChart';
import { observabilityService, type MetricResult } from '../../services/observability.service';
import { RefreshCw, Clock, Activity, Cpu } from 'lucide-react';
import EdgeSelector from '../../components/common/EdgeSelector';
import { TimeRange } from '../../components/observability/TimeRange';
import {
  DEFAULT_TIME_RANGE,
  resolveTimeRange,
  type TimeRangeValue,
} from '../../utils/timeRange';
import { useSettingsStore } from '../../stores/settingsStore';
import { formatTimeOnly } from '../../utils/formatDate';

/** One recharts row: an x value plus one numeric key per series. */
type ChartPoint = { timestamp: number } & Record<string, number>;

// Helper to transform Prometheus matrix data to Recharts format
const transformMetricData = (results: MetricResult[], keyName: string) => {
  if (!results || results.length === 0) return [];

  // For multiple series (e.g. interfaces), we need to flatten them
  const dataMap = new Map<number, ChartPoint>();

  results.forEach((series) => {
    // Use interface name for label if available, otherwise generic
    const seriesLabel = series.metric.interface ? `${keyName}_${series.metric.interface}` : keyName;

    series.values?.forEach(([ts, val]) => {
      const point = dataMap.get(ts) ?? { timestamp: ts };
      point[seriesLabel] = parseFloat(val);
      dataMap.set(ts, point);
    });
  });

  return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

// Helper for multi-series data (network errors, etc.)
const transformMetricDataMulti = (results: MetricResult[]) => {
  if (!results || results.length === 0) return [];

  const dataMap = new Map<number, ChartPoint>();

  results.forEach((series) => {
    const iface = series.metric.interface || 'default';
    const type = series.metric.type || 'value';
    const seriesLabel = `${iface} ${type}`;

    series.values?.forEach(([ts, val]) => {
      const point = dataMap.get(ts) ?? { timestamp: ts };
      point[seriesLabel] = parseFloat(val);
      dataMap.set(ts, point);
    });
  });

  return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

// Helper for WireGuard data
const transformWireGuardData = (results: MetricResult[]) => {
  if (!results || results.length === 0) return [];

  const dataMap = new Map<number, ChartPoint>();

  results.forEach((series) => {
    const seriesLabel = series.metric.series || 'value';

    series.values?.forEach(([ts, val]) => {
      const point = dataMap.get(ts) ?? { timestamp: ts };
      point[seriesLabel] = parseFloat(val);
      dataMap.set(ts, point);
    });
  });

  return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

// Helper for VPP interface data (bytes)
const transformVPPData = (results: MetricResult[]) => {
  if (!results || results.length === 0) return [];

  const dataMap = new Map<number, ChartPoint>();

  results.forEach((series) => {
    const name = series.metric.name || 'unknown';
    const direction = series.metric.direction || '';
    const seriesLabel = `${name} ${direction}`;

    series.values?.forEach(([ts, val]) => {
      const point = dataMap.get(ts) ?? { timestamp: ts };
      point[seriesLabel] = parseFloat(val);
      dataMap.set(ts, point);
    });
  });

  return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

// Helper for VPP errors data
const transformVPPErrorsData = (results: MetricResult[]) => {
  if (!results || results.length === 0) return [];

  const dataMap = new Map<number, ChartPoint>();

  results.forEach((series) => {
    const name = series.metric.name || 'unknown';
    const type = series.metric.type || 'error';
    const seriesLabel = `${name} ${type}`;

    series.values?.forEach(([ts, val]) => {
      const point = dataMap.get(ts) ?? { timestamp: ts };
      point[seriesLabel] = parseFloat(val);
      dataMap.set(ts, point);
    });
  });

  return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

// Format uptime duration
const formatUptime = (seconds: number | null) => {
  if (seconds === null) return 'N/A';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${days}d ${hours}h ${minutes}m`;
};

// Colors
const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#e11d48'];

// Helper to get dynamic lines for a chart
const getDynamicLines = (data: ChartPoint[], excludeKeys = ["timestamp"]) => {
  if (!data || data.length === 0) return [];
  const keys = Object.keys(data[0] || {}).filter(k => !excludeKeys.includes(k));
  return keys.map((k, i) => ({
    dataKey: k,
    name: k,
    color: COLORS[i % COLORS.length]
  }));
};


const ObservabilityDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const edgeIdParam = searchParams.get('edgeId');

  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [selectedEdgeId, setSelectedEdgeId] = useState<string>(edgeIdParam || '');
  // VSNP-1218: the window was hard-wired to 1 h; it is now the shared picker.
  const [range, setRange] = useState<TimeRangeValue>(DEFAULT_TIME_RANGE);

  // Metrics State
  const [uptime, setUptime] = useState<number | null>(null);
  const [mqttStatus, setMqttStatus] = useState<number | null>(null);
  const [cpuUsage, setCpuUsage] = useState<number | null>(null);
  const [cpuHistoricalData, setCpuHistoricalData] = useState<ChartPoint[]>([]);
  const [memoryData, setMemoryData] = useState<ChartPoint[]>([]);
  const [netRxData, setNetRxData] = useState<ChartPoint[]>([]);
  const [netTxData, setNetTxData] = useState<ChartPoint[]>([]);
  const [netErrorsData, setNetErrorsData] = useState<ChartPoint[]>([]);
  // WireGuard metrics
  const [wgServerEnabled, setWgServerEnabled] = useState<number | null>(null);
  const [wgPeerCount, setWgPeerCount] = useState<number | null>(null);
  const [wgPeersEstablished, setWgPeersEstablished] = useState<number | null>(null);
  const [wgServerPort, setWgServerPort] = useState<number | null>(null);
  const [wgPeerCountData, setWgPeerCountData] = useState<ChartPoint[]>([]);
  const [vppBytesData, setVppBytesData] = useState<ChartPoint[]>([]);
  const [vppErrorsData, setVppErrorsData] = useState<ChartPoint[]>([]);


  const fetchData = useCallback(async (isBackground = false) => {
    if (!selectedEdgeId) return;

    if (!isBackground) setLoading(true);
    try {
      const options = isBackground ? { showLoading: false } : undefined;
      // Resolved HERE, per fetch: a relative range must mean "now", not the
      // instant the user picked it (see components/observability/TimeRange).
      const { start, end, step, window: rateWindow } = resolveTimeRange(range);

      // 1. Range queries for Stats/Gauges (using lastNotNull like Grafana)
      // Same window as the charts, so a gauge is never blank while a chart has data.
      const statusStart = start;
      const statusStep = step;

      const uptimeRes = await observabilityService.queryRange(
        `edge_uptime_seconds{edge_id="${selectedEdgeId}"}`,
        statusStart, end, statusStep, options
      );

      const mqttRes = await observabilityService.queryRange(
        `edge_mqtt_connected{edge_id="${selectedEdgeId}"}`,
        statusStart, end, statusStep, options
      );
      const cpuRes = await observabilityService.queryRange(
        `edge_cpu_usage_percent{edge_id="${selectedEdgeId}"}`,
        statusStart, end, statusStep, options
      );

      // 2. Range queries for Charts
      // Note: Using Prometheus text format - no _val suffix needed
      const cpuHistRes = await observabilityService.queryRange(
        `edge_cpu_usage_percent{edge_id="${selectedEdgeId}"}`,
        start, end, step, options
      );

      const memRes = await observabilityService.queryRange(
        `edge_memory_bytes{edge_id="${selectedEdgeId}"} / 1024 / 1024`,
        start, end, step, options
      );



      const netRxRes = await observabilityService.queryRange(
        `rate(edge_interface_rx_bytes_total{edge_id="${selectedEdgeId}"}[${rateWindow}])`,
        start, end, step, options
      );

      const netTxRes = await observabilityService.queryRange(
        `rate(edge_interface_tx_bytes_total{edge_id="${selectedEdgeId}"}[${rateWindow}])`,
        start, end, step, options
      );

      // Network Errors & Drops (4 series)
      const netRxErrorsRes = await observabilityService.queryRange(
        `rate(edge_interface_rx_errors_total{edge_id="${selectedEdgeId}"}[${rateWindow}])`,
        start, end, step, options
      );
      const netTxErrorsRes = await observabilityService.queryRange(
        `rate(edge_interface_tx_errors_total{edge_id="${selectedEdgeId}"}[${rateWindow}])`,
        start, end, step, options
      );
      const netRxDroppedRes = await observabilityService.queryRange(
        `rate(edge_interface_rx_dropped_total{edge_id="${selectedEdgeId}"}[${rateWindow}])`,
        start, end, step, options
      );
      const netTxDroppedRes = await observabilityService.queryRange(
        `rate(edge_interface_tx_dropped_total{edge_id="${selectedEdgeId}"}[${rateWindow}])`,
        start, end, step, options
      );

      // WireGuard metrics
      const wgServerEnabledRes = await observabilityService.queryRange(
        `edge_wireguard_server_enabled{edge_id="${selectedEdgeId}"}`,
        statusStart, end, statusStep, options
      );
      const wgPeerCountRes = await observabilityService.queryRange(
        `edge_wireguard_peer_count{edge_id="${selectedEdgeId}"}`,
        statusStart, end, statusStep, options
      );
      const wgPeersEstablishedRes = await observabilityService.queryRange(
        `edge_wireguard_peers_established_total{edge_id="${selectedEdgeId}"}`,
        statusStart, end, statusStep, options
      );
      const wgServerPortRes = await observabilityService.queryRange(
        `edge_wireguard_server_port{edge_id="${selectedEdgeId}"}`,
        statusStart, end, statusStep, options
      );
      const wgPeerCountHistRes = await observabilityService.queryRange(
        `edge_wireguard_peer_count{edge_id="${selectedEdgeId}"}`,
        start, end, step, options
      );
      const wgPeersEstablishedHistRes = await observabilityService.queryRange(
        `edge_wireguard_peers_established_total{edge_id="${selectedEdgeId}"}`,
        start, end, step, options
      );

      // VPP Interface metrics
      const vppRxBytesRes = await observabilityService.queryRange(
        `rate(vpp_interfaces_rx_bytes{agent=~".*", name=~".*"}[${rateWindow}])`,
        start, end, step, options
      );
      const vppTxBytesRes = await observabilityService.queryRange(
        `rate(vpp_interfaces_tx_bytes{agent=~".*", name=~".*"}[${rateWindow}])`,
        start, end, step, options
      );
      const vppRxErrorsRes = await observabilityService.queryRange(
        `rate(vpp_interfaces_rx_errors{agent=~".*", name=~".*"}[${rateWindow}])`,
        start, end, step, options
      );
      const vppTxErrorsRes = await observabilityService.queryRange(
        `rate(vpp_interfaces_tx_errors{agent=~".*", name=~".*"}[${rateWindow}])`,
        start, end, step, options
      );
      const vppDropsRes = await observabilityService.queryRange(
        `rate(vpp_interfaces_drops{agent=~".*", name=~".*"}[${rateWindow}])`,
        start, end, step, options
      );

      // Process Results - Extract last non-null value from range queries (lastNotNull)
      // Helper function to get last non-null value from a range query result
      const getLastNotNull = (result: MetricResult[]): number | null => {
        if (!result || result.length === 0) {
          console.debug('getLastNotNull: empty result');
          return null;
        }
        // For range queries, we have values array with [timestamp, value] pairs
        // Find the last non-null value across all series
        let lastValue: number | null = null;
        let lastTimestamp = 0;

        for (const series of result) {
          if (series.values && series.values.length > 0) {
            // values are sorted by timestamp, so get the last one
            const lastPoint = series.values[series.values.length - 1];
            if (lastPoint && lastPoint[1] !== 'NaN' && lastPoint[1] !== '') {
              const timestamp = lastPoint[0];
              const value = parseFloat(lastPoint[1]);
              if (!isNaN(value) && timestamp > lastTimestamp) {
                lastValue = value;
                lastTimestamp = timestamp;
              }
            }
          }
        }
        console.debug('getLastNotNull: result length=', result.length, 'lastValue=', lastValue);
        return lastValue;
      };

      // Debug logging
      console.debug('Status panel query results:', {
        uptime: { status: uptimeRes.status, resultCount: uptimeRes.data?.result?.length || 0, result: uptimeRes.data?.result },
        mqtt: { status: mqttRes.status, resultCount: mqttRes.data?.result?.length || 0, result: mqttRes.data?.result },
        cpu: { status: cpuRes.status, resultCount: cpuRes.data?.result?.length || 0, result: cpuRes.data?.result },
      });

      if (uptimeRes.status === 'success') {
        const value = getLastNotNull(uptimeRes.data.result);
        console.debug('Uptime value:', value);
        if (value !== null) setUptime(value);
      } else {
        console.warn('Uptime query failed:', uptimeRes);
      }

      if (mqttRes.status === 'success') {
        const value = getLastNotNull(mqttRes.data.result);
        console.debug('MQTT status value:', value);
        if (value !== null) setMqttStatus(value);
      } else {
        console.warn('MQTT query failed:', mqttRes);
      }
      if (cpuRes.status === 'success') {
        const value = getLastNotNull(cpuRes.data.result);
        console.debug('CPU value:', value);
        if (value !== null) setCpuUsage(value);
      } else {
        console.warn('CPU query failed:', cpuRes);
      }

      if (cpuHistRes.status === 'success') setCpuHistoricalData(transformMetricData(cpuHistRes.data.result, 'cpu'));
      if (memRes.status === 'success') setMemoryData(transformMetricData(memRes.data.result, 'memory'));

      if (netRxRes.status === 'success') setNetRxData(transformMetricData(netRxRes.data.result, 'rx'));
      if (netTxRes.status === 'success') setNetTxData(transformMetricData(netTxRes.data.result, 'tx'));

      // Combine network errors and drops into single dataset
      const netErrorsResults = [
        ...(netRxErrorsRes.status === 'success' ? netRxErrorsRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, type: 'RX Errors' } })) : []),
        ...(netTxErrorsRes.status === 'success' ? netTxErrorsRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, type: 'TX Errors' } })) : []),
        ...(netRxDroppedRes.status === 'success' ? netRxDroppedRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, type: 'RX Dropped' } })) : []),
        ...(netTxDroppedRes.status === 'success' ? netTxDroppedRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, type: 'TX Dropped' } })) : [])
      ];
      setNetErrorsData(transformMetricDataMulti(netErrorsResults));

      // WireGuard data
      if (wgServerEnabledRes.status === 'success') {
        const value = getLastNotNull(wgServerEnabledRes.data.result);
        if (value !== null) setWgServerEnabled(value);
      }
      if (wgPeerCountRes.status === 'success') {
        const value = getLastNotNull(wgPeerCountRes.data.result);
        if (value !== null) setWgPeerCount(value);
      }
      if (wgPeersEstablishedRes.status === 'success') {
        const value = getLastNotNull(wgPeersEstablishedRes.data.result);
        if (value !== null) setWgPeersEstablished(value);
      }
      if (wgServerPortRes.status === 'success') {
        const value = getLastNotNull(wgServerPortRes.data.result);
        if (value !== null) setWgServerPort(value);
      }
      // Combine peer count and established for chart
      if (wgPeerCountHistRes.status === 'success' || wgPeersEstablishedHistRes.status === 'success') {
        const peerCountResults = wgPeerCountHistRes.status === 'success'
          ? wgPeerCountHistRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, series: 'Total Peers' } }))
          : [];
        const establishedResults = wgPeersEstablishedHistRes.status === 'success'
          ? wgPeersEstablishedHistRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, series: 'Established' } }))
          : [];
        setWgPeerCountData(transformWireGuardData([...peerCountResults, ...establishedResults]));
      }

      // VPP data - combine RX/TX into one dataset
      const vppBytesResults = [
        ...(vppRxBytesRes.status === 'success' ? vppRxBytesRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, direction: 'RX' } })) : []),
        ...(vppTxBytesRes.status === 'success' ? vppTxBytesRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, direction: 'TX' } })) : [])
      ];
      setVppBytesData(transformVPPData(vppBytesResults));

      const vppErrorsResults = [
        ...(vppRxErrorsRes.status === 'success' ? vppRxErrorsRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, type: 'RX Errors' } })) : []),
        ...(vppTxErrorsRes.status === 'success' ? vppTxErrorsRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, type: 'TX Errors' } })) : []),
        ...(vppDropsRes.status === 'success' ? vppDropsRes.data.result.map((r: MetricResult) => ({ ...r, metric: { ...r.metric, type: 'Drops' } })) : [])
      ];
      setVppErrorsData(transformVPPErrorsData(vppErrorsResults));

      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [selectedEdgeId, range]);

  useEffect(() => {
    void fetchData(false);
    const interval = setInterval(() => void fetchData(true), 30000); // 30s refresh
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Edge Device Overview</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time metrics for connected edges</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500 hidden sm:flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            {formatTimeOnly(lastRefreshed, dateSettings)}
          </span>
          <TimeRange value={range} onChange={setRange} />
          <button
            onClick={() => void fetchData(false)}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Edge Selection */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Edge Device</label>
          <EdgeSelector
            selectedEdgeId={selectedEdgeId}
            onEdgeChange={setSelectedEdgeId}
          />
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Uptime */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Edge Uptime</h3>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-3xl font-semibold text-gray-900">{formatUptime(uptime)}</p>
        </div>

        {/* MQTT Status */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">MQTT Status</h3>
            <Activity className="h-5 w-5 text-gray-400" />
          </div>
          <div className={`mt-2 text-lg font-semibold flex items-center ${mqttStatus === 1 ? 'text-green-600' : 'text-red-600'}`}>
            <span className={`h-3 w-3 rounded-full mr-2 ${mqttStatus === 1 ? 'bg-green-500' : 'bg-red-500'}`}></span>
            {mqttStatus === 1 ? 'Connected' : 'Disconnected'}
          </div>
        </div>

        {/* CPU Gauge */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">CPU Usage</h3>
            <Cpu className="h-5 w-5 text-gray-400" />
          </div>
          <div className="mt-2">
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-3xl font-semibold text-gray-900">{cpuUsage?.toFixed(1) || '0'}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full ${(cpuUsage || 0) > 80 ? 'bg-red-600' :
                  (cpuUsage || 0) > 60 ? 'bg-yellow-500' : 'bg-green-600'
                  }`}
                style={{ width: `${Math.min(cpuUsage || 0, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid - Following Grafana Dashboard Layout */}

      {/* Row 2: CPU Historical + Memory */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="CPU Usage (Historical)"
          data={cpuHistoricalData}
          loading={loading}
          unit="%"
          lines={[{ dataKey: 'cpu', name: 'CPU Usage', color: COLORS[0] }]}
        />

        <MetricsChart
          title="Memory Usage"
          data={memoryData}
          loading={loading}
          unit=" MB"
          lines={[{ dataKey: 'memory', name: 'Memory Usage', color: COLORS[1] }]}
        />
      </div>

      {/* Row 3: Network RX + Network TX */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="Network RX (Bytes/sec)"
          data={netRxData}
          loading={loading}
          unit=" B/s"
          lines={getDynamicLines(netRxData)}
        />

        <MetricsChart
          title="Network TX (Bytes/sec)"
          data={netTxData}
          loading={loading}
          unit=" B/s"
          lines={getDynamicLines(netTxData)}
        />
      </div>

      {/* Row 4: Network Errors & Drops (full width) */}
      <div className="grid grid-cols-1 gap-6">
        <MetricsChart
          title="Network Errors & Drops"
          data={netErrorsData}
          loading={loading}
          unit=" ops/s"
          lines={getDynamicLines(netErrorsData)}
        />
      </div>

      {/* Row 5: Gateway Tunnel Metrics - status cards and chart */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tunnel Server Status */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Tunnel Server</h3>
          </div>
          <div className={`mt-2 text-lg font-semibold flex items-center ${wgServerEnabled === 1 ? 'text-green-600' : 'text-red-600'}`}>
            <span className={`h-3 w-3 rounded-full mr-2 ${wgServerEnabled === 1 ? 'bg-green-500' : 'bg-red-500'}`}></span>
            {wgServerEnabled === 1 ? 'Enabled' : 'Disabled'}
          </div>
        </div>

        {/* WireGuard Peer Count */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Total Peers</h3>
          </div>
          <p className="mt-2 text-3xl font-semibold text-blue-600">{wgPeerCount ?? 0}</p>
        </div>

        {/* WireGuard Peers Established */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Peers Established</h3>
          </div>
          <p className="mt-2 text-3xl font-semibold text-green-600">{wgPeersEstablished ?? 0}</p>
        </div>

        {/* WireGuard Listen Port */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Listen Port</h3>
          </div>
          <p className="mt-2 text-3xl font-semibold text-gray-900">{wgServerPort ?? 'N/A'}</p>
        </div>
      </div>

      {/* WireGuard Historical Chart */}
      <div className="grid grid-cols-1 gap-6">
        <MetricsChart
          title="Tunnel Peer Status (Historical)"
          data={wgPeerCountData}
          loading={loading}
          unit=""
          lines={getDynamicLines(wgPeerCountData)}
        />
      </div>

      {/* Row 6: VPP Interface Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="VPP Interfaces (Bytes/sec)"
          data={vppBytesData}
          loading={loading}
          unit=" B/s"
          lines={getDynamicLines(vppBytesData)}
        />

        <MetricsChart
          title="VPP Interfaces (Errors & Drops)"
          data={vppErrorsData}
          loading={loading}
          unit=" ops/s"
          lines={getDynamicLines(vppErrorsData)}
        />
      </div>
    </div>
  );
};

export default ObservabilityDashboard;
